import { useCallback, useEffect, useState } from "react";
import { demoApi } from "@/api/client";
import type { PieceTicket, PieceType } from "@/api/types";
import { describeReadFailure } from "@/lib/apiFailure";

export type PieceTicketsStatus = "loading" | "ready" | "error";

export interface UsePieceTicketsResult {
  /** `null` until the first read resolves and after a failed one — never `[]` for "unknown". */
  tickets: PieceTicket[] | null;
  status: PieceTicketsStatus;
  error: string | null;
  /** Re-reads the list; the current rows stay on screen while it runs. */
  refetch: () => void;
}

/** The API's own ceiling for `limit` (`PieceTicketEndpoints.MaxListLimit`). */
const LIST_LIMIT = 500;

/**
 * ef-060-7 — the People tab's piece tickets: OPEN ones by default, every status (newest first)
 * when `includeClosed` is on. The filter is the server's (`?status=open`), never a client-side
 * hide, so "Show paid and void" is a real second read.
 */
export function usePieceTickets(includeClosed: boolean): UsePieceTicketsResult {
  const [tickets, setTickets] = useState<PieceTicket[] | null>(null);
  const [status, setStatus] = useState<PieceTicketsStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);
  const refetch = useCallback(() => setReloads((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    demoApi
      .listPieceTickets(includeClosed ? { limit: LIST_LIMIT } : { status: "open", limit: LIST_LIMIT }, controller.signal)
      .then((rows) => {
        if (cancelled) return;
        setTickets(rows);
        setStatus("ready");
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        setTickets(null);
        setStatus("error");
        setError(describeReadFailure(err));
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [includeClosed, reloads]);

  return { tickets, status, error, refetch };
}

export interface UsePieceTypeChoicesResult {
  /** Current (non-archived) piece types — what the ticket dialog's picker offers. */
  types: PieceType[] | null;
  status: PieceTicketsStatus;
  refetch: () => void;
}

/** Current piece types for the picker. A failure leaves `types` null; the dialog says so. */
export function usePieceTypeChoices(): UsePieceTypeChoicesResult {
  const [types, setTypes] = useState<PieceType[] | null>(null);
  const [status, setStatus] = useState<PieceTicketsStatus>("loading");
  const [reloads, setReloads] = useState(0);
  const refetch = useCallback(() => setReloads((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    demoApi
      .listPieceTypes(false, controller.signal)
      .then((rows) => {
        if (cancelled) return;
        setTypes(rows);
        setStatus("ready");
      })
      .catch((err: unknown) => {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        setTypes(null);
        setStatus("error");
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [reloads]);

  return { types, status, refetch };
}
